import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { API, api, apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const SESSION_ID = "assistant-main";

export default function Chatbot() {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      api
        .get(`/chat/history?session_id=${SESSION_ID}`)
        .then((res) => setMessages(res.data))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    // Empty assistant placeholder, filled in incrementally as chunks arrive —
    // the bouncing-dots indicator below covers the "still empty" moment.
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: SESSION_ID, message: text }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          detail = apiErrorMessage((await res.json()).detail) || detail;
        } catch (_) { /* body wasn't JSON — keep the status-based message */ }
        throw new Error(detail);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (!chunk) continue;
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + chunk };
          return next;
        });
      }
    } catch (e) {
      toast.error(e.message || "Error");
      setMessages((m) => m.slice(0, -1)); // drop the empty placeholder on failure
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        whileTap={{ scale: 0.92 }}
        onClick={() => setOpen((o) => !o)}
        data-testid="chatbot-toggle"
        className="fixed bottom-24 lg:bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-[hsl(var(--success))] text-white shadow-lg shadow-emerald-900/30 flex items-center justify-center"
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chatbot-panel"
            className="fixed bottom-40 lg:bottom-24 right-6 z-50 w-[90vw] max-w-sm h-[70vh] max-h-[560px] rounded-lg border border-white/10 bg-background/80 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-2 px-4 h-14 border-b border-border shrink-0">
              <Sparkles className="h-4 w-4 text-[hsl(var(--success))]" />
              <span className="font-display text-lg">{t("chat_title")}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="chatbot-close"
                aria-label="Close"
                className="ml-auto p-1 rounded-sm text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 && !loading && (
                <p className="text-sm text-muted-foreground text-center mt-8">
                  {t("chat_placeholder")}
                </p>
              )}
              {messages.map((m, i) => (
                m.content ? (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-foreground"
                      }`}
                    >
                      {m.content}
                    </div>
                  </div>
                ) : null
              ))}
              {loading && !messages[messages.length - 1]?.content && (
                <div className="flex justify-start">
                  <div className="bg-secondary rounded-lg px-3 py-2 flex gap-1">
                    <span className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce" />
                    <span className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.15s]" />
                    <span className="h-2 w-2 rounded-full bg-muted-foreground animate-bounce [animation-delay:0.3s]" />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-border flex gap-2 shrink-0">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={t("chat_placeholder")}
                data-testid="chatbot-input"
                className="rounded-sm"
              />
              <Button onClick={send} size="icon" data-testid="chatbot-send" className="rounded-sm shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
